import { ApiError } from "@/api/ApiError";
import { getVisitUid, loadAccessToken, setVisitUid } from "@/utils/storage";
import { getApiHost, getTld } from "@/utils/urlHelper";
import { getCookie } from "@/utils/cookies.ts";
import { ENV_PRODUCTION } from "@/utils/variables.ts";

type HttpMethod = "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
type RequestOptions = {
  simpleCors?: boolean;
};

const RETRY_LIMIT = 5;
const PROXY_ROOT = `${location.origin}/service/api`;
const stripObject = (obj: any) =>
  Object.keys(obj)
    .filter((k) => obj[k] != null)
    .reduce((a, k) => ({ ...a, [k]: obj[k] }), {});

const createHeaders = (isFormData: boolean, method: HttpMethod, options: RequestOptions): Record<string, string> => {
  if (options.simpleCors && method === "GET") {
    const headers: Record<string, string> = {
      Accept: "application/json",
    };

    const token = loadAccessToken();

    if (token) {
      headers.Authorization = `Bearer ${token}`;
    }

    return headers;
  }

  const headers: Record<string, string> = {
    Accept: "application/json",
    "X-Source-Tld": getTld(),
    "X-Visit-Uid": getVisitUid(),
  };

  if (!isFormData && method !== "GET") {
    headers["Content-Type"] = "application/json";
  }

  const token = loadAccessToken();

  if (token) {
    headers.Authorization = `Bearer ${token}`;
  }

  return headers;
};

const handleResponse = async (response: Response) => {
  const visitUid = response.headers.get("X-Visit-Uid");

  if (visitUid) {
    setVisitUid(visitUid);
  }

  if (response.status === 204) {
    return {};
  }

  const contentType = response.headers.get("content-type");

  if (!contentType || !contentType.includes("application/json")) {
    const text = await response.text();
    throw new ApiError(text, { status: response.status });
  }

  const body = await response.json();

  if (response.status >= 400) {
    throw new ApiError(response.statusText, {
      status: response.status,
      ...body,
    });
  }

  return body;
};

const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const makeRequest = async (
  method: HttpMethod,
  path: string,
  params?: object,
  signal: AbortSignal | null = null,
  count: number = 0,
  useProxy: boolean = false,
  options: RequestOptions = {},
) => {
  useProxy = useProxy || !!getCookie("use_proxy");
  const url = new URL(useProxy ? PROXY_ROOT : getApiHost());

  if (useProxy) {
    url.searchParams.set("route", path);
  } else {
    url.pathname = path;
  }

  let requestBody: FormData | string | null = null;

  if (params instanceof FormData) {
    requestBody = params as FormData;
  } else if (typeof params === "object") {
    if (method === "GET") {
      const searchParams = new URLSearchParams(stripObject(params));
      searchParams.forEach((value, key) => {
        url.searchParams.set(key, value);
      });
    } else {
      requestBody = JSON.stringify(params);
    }
  }

  const headers = createHeaders(requestBody instanceof FormData, method, options);
  let response: Response | null = null;

  if (count > 0) {
    await delay(250 * count);
  }

  try {
    response = await fetch(url.toString(), {
      method,
      headers,
      body: requestBody,
      credentials: "include",
      mode: "cors",
      cache: "no-store",
      referrerPolicy: "unsafe-url",
      signal,
    });
  } catch (error) {
    if (error instanceof Error && error.name === "AbortError") {
      return null;
    }
  }

  if (!response || response?.status >= 500) {
    if (count < RETRY_LIMIT) {
      return makeRequest(method, path, params, signal, count + 1, import.meta.env.VITE_ENV === ENV_PRODUCTION, options);
    }

    throw new ApiError(response?.statusText || "Retry limit reached", {
      status: response?.status,
      path,
      errorMessage: {
        code: "MaxRetries",
      },
    });
  }

  return await handleResponse(response);
};

const api = {
  get: (path: string, params?: object, signal?: AbortSignal | null) => makeRequest("GET", path, params, signal),
  getSimple: (path: string, params?: object, signal?: AbortSignal | null) =>
    makeRequest("GET", path, params, signal, 0, false, { simpleCors: true }),
  post: (path: string, params?: object, signal?: AbortSignal | null) => makeRequest("POST", path, params, signal),
  put: (path: string, params?: object, signal?: AbortSignal | null) => makeRequest("PUT", path, params, signal),
  patch: (path: string, params?: object, signal?: AbortSignal | null) => makeRequest("PATCH", path, params, signal),
  delete: (path: string, params?: object, signal?: AbortSignal | null) => makeRequest("DELETE", path, params, signal),
};

export default api;
